import type { ExcalidrawElementSkeleton } from "@excalidraw/element";

import { layoutGeneratedBoard } from "./layoutGeneratedBoard";

type Mutable<T> = {
  -readonly [Key in keyof T]: T[Key];
};

type GeneratedElement = Mutable<ExcalidrawElementSkeleton> &
  Record<string, any>;

const SAFE_TEXT_COLOR = "#1e1e1e";
const SAFE_SHAPE_BACKGROUNDS = [
  "#dbeafe",
  "#dcfce7",
  "#ede9fe",
  "#fef3c7",
  "#f8fafc",
];
const SAFE_SHAPE_STROKES = [
  "#2563eb",
  "#16a34a",
  "#7c3aed",
  "#f59e0b",
  "#475569",
];

const SHAPE_TYPES = new Set(["rectangle", "diamond", "ellipse"]);
const LINEAR_TYPES = new Set(["arrow", "line"]);

const isTransparentColor = (color: unknown) => {
  if (typeof color !== "string") {
    return true;
  }

  const normalized = color.trim().toLowerCase();
  return (
    !normalized ||
    normalized === "transparent" ||
    normalized === "none" ||
    normalized === "rgba(0,0,0,0)" ||
    normalized === "rgba(0, 0, 0, 0)"
  );
};

const parseColor = (color: string) => {
  const normalized = color.trim().toLowerCase();

  const shortHexMatch = normalized.match(/^#([a-f0-9]{3})$/i);
  if (shortHexMatch) {
    const [r, g, b] = shortHexMatch[1].split("").map((part) => {
      const doubled = `${part}${part}`;
      return parseInt(doubled, 16);
    });
    return { r, g, b, alpha: 1 };
  }

  const hexMatch = normalized.match(/^#([a-f0-9]{6})$/i);
  if (hexMatch) {
    return {
      r: parseInt(hexMatch[1].slice(0, 2), 16),
      g: parseInt(hexMatch[1].slice(2, 4), 16),
      b: parseInt(hexMatch[1].slice(4, 6), 16),
      alpha: 1,
    };
  }

  const rgbaMatch = normalized.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/,
  );
  if (rgbaMatch) {
    return {
      r: Number(rgbaMatch[1]),
      g: Number(rgbaMatch[2]),
      b: Number(rgbaMatch[3]),
      alpha: typeof rgbaMatch[4] === "string" ? Number(rgbaMatch[4]) : 1,
    };
  }

  return null;
};

const isLightOrInvisibleColor = (color: unknown) => {
  if (isTransparentColor(color)) {
    return true;
  }

  if (typeof color !== "string") {
    return true;
  }

  const parsedColor = parseColor(color);
  if (!parsedColor) {
    return false;
  }

  if (parsedColor.alpha <= 0.2) {
    return true;
  }

  const luminance =
    (0.2126 * parsedColor.r + 0.7152 * parsedColor.g + 0.0722 * parsedColor.b) /
    255;

  return luminance >= 0.82;
};

const getMinimumNumber = (value: unknown, minimum: number) => {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(value, minimum)
    : minimum;
};

const estimateTextHeight = (text: unknown, width: number, fontSize: number) => {
  if (typeof text !== "string" || !text.trim()) {
    return Math.ceil(fontSize * 1.3);
  }

  const charsPerLine = Math.max(16, Math.floor(width / (fontSize * 0.52)));
  const lineCount = text.split("\n").reduce((count, line) => {
    return count + Math.max(1, Math.ceil(line.length / charsPerLine));
  }, 0);

  return Math.ceil(lineCount * fontSize * 1.3);
};

const normalizeLabel = (label: unknown) => {
  if (!label || typeof label !== "object") {
    return label;
  }

  const normalizedLabel = { ...(label as Record<string, unknown>) };
  normalizedLabel.strokeColor = SAFE_TEXT_COLOR;
  normalizedLabel.fontSize = getMinimumNumber(normalizedLabel.fontSize, 22);

  if (
    typeof normalizedLabel.text !== "string" ||
    !normalizedLabel.text.trim()
  ) {
    normalizedLabel.text = "Texto";
  }

  return normalizedLabel;
};

const getOrder = (type: unknown) => {
  if (typeof type !== "string") {
    return 1;
  }

  if (SHAPE_TYPES.has(type)) {
    return 0;
  }

  if (LINEAR_TYPES.has(type)) {
    return 1;
  }

  if (type === "text") {
    return 2;
  }

  return 1;
};

export const normalizeGeneratedExcalidrawElements = (
  elements: readonly ExcalidrawElementSkeleton[],
): ExcalidrawElementSkeleton[] => {
  const sourceElements = elements as readonly GeneratedElement[];
  const lightOrInvisibleElementsCount = sourceElements.filter((element) => {
    return (
      isLightOrInvisibleColor(element.strokeColor) ||
      (SHAPE_TYPES.has(element.type) &&
        isLightOrInvisibleColor(element.backgroundColor)) ||
      (element.type === "text" && isLightOrInvisibleColor(element.strokeColor))
    );
  }).length;
  const shouldForceSafePalette =
    sourceElements.length > 0 &&
    lightOrInvisibleElementsCount / sourceElements.length > 0.5;

  const normalizedElementsWithIndexes = sourceElements.map((element, index) => {
    const normalizedElement: GeneratedElement = {
      ...element,
      isDeleted: false,
      opacity: 100,
      roughness:
        typeof element.roughness === "number" ? element.roughness : 0.8,
    };

    if (isLightOrInvisibleColor(normalizedElement.strokeColor)) {
      normalizedElement.strokeColor = SAFE_TEXT_COLOR;
    }

    if (normalizedElement.type === "text") {
      normalizedElement.strokeColor = SAFE_TEXT_COLOR;
      normalizedElement.backgroundColor = "transparent";
      normalizedElement.fontSize = getMinimumNumber(
        normalizedElement.fontSize,
        22,
      );
      normalizedElement.width = getMinimumNumber(normalizedElement.width, 180);
      normalizedElement.height = getMinimumNumber(
        normalizedElement.height,
        estimateTextHeight(
          normalizedElement.text,
          normalizedElement.width,
          normalizedElement.fontSize,
        ),
      );

      if (
        typeof normalizedElement.text !== "string" ||
        !normalizedElement.text.trim()
      ) {
        normalizedElement.text = "Texto";
      }
    }

    if (SHAPE_TYPES.has(normalizedElement.type)) {
      const paletteIndex = index % SAFE_SHAPE_BACKGROUNDS.length;

      normalizedElement.strokeWidth = getMinimumNumber(
        normalizedElement.strokeWidth,
        2,
      );
      normalizedElement.width = getMinimumNumber(normalizedElement.width, 260);
      normalizedElement.height = getMinimumNumber(
        normalizedElement.height,
        140,
      );
      normalizedElement.fillStyle = "solid";

      if (
        shouldForceSafePalette ||
        isLightOrInvisibleColor(normalizedElement.backgroundColor)
      ) {
        normalizedElement.backgroundColor =
          SAFE_SHAPE_BACKGROUNDS[paletteIndex];
      }

      normalizedElement.strokeColor = SAFE_SHAPE_STROKES[paletteIndex];

      normalizedElement.label = normalizeLabel(normalizedElement.label);
    }

    if (LINEAR_TYPES.has(normalizedElement.type)) {
      normalizedElement.strokeColor = SAFE_TEXT_COLOR;
      normalizedElement.strokeWidth = 2;
      normalizedElement.opacity = 100;
    }

    return {
      element: normalizedElement,
      index,
    };
  });
  const normalizedElementsByOriginalOrder = layoutGeneratedBoard(
    normalizedElementsWithIndexes
      .slice()
      .sort((firstEntry, secondEntry) => firstEntry.index - secondEntry.index)
      .map(({ element }) => element),
  ) as GeneratedElement[];
  const normalizedElements = normalizedElementsByOriginalOrder
    .slice()
    .map((element, index) => ({ element, index }))
    .sort((firstEntry, secondEntry) => {
      const orderDifference =
        getOrder(firstEntry.element.type) - getOrder(secondEntry.element.type);

      return orderDifference || firstEntry.index - secondEntry.index;
    })
    .map(({ element }) => element);

  return normalizedElements as ExcalidrawElementSkeleton[];
};
